import React from 'react';
import styled from 'styled-components';
import { FaUser } from 'react-icons/fa';
import { Input } from '../../../components/ui/Input/Input';
import { Select } from '../../../components/ui/Select/Select';
import { SearchableSelect } from '../../../components/ui/SearchableSelect/SearchableSelect';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { SEXO_OPTIONS, ESTADO_CIVIL_OPTIONS } from '../../../utils/constants';
import { formatCPFCNPJ, unformat } from '../../../utils/formatters';
import { useMunicipioSearch } from '../../../hooks/useMunicipioSearch';

const getMunicipioLabel = (mun) => mun ? `${mun.nome} - ${mun.estadoSigla}` : '';

const Section = styled.section`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8ecef;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.8rem;
  border-bottom: 2px solid ${colors.lightBlue};
`;

const Icon = styled.div`
  color: ${colors.darkBlue};
  font-size: 1.2rem;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.darkBlue};
  margin: 0;
`;

const Description = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.3rem;
`;

const Row = styled.div`
  display: grid;
  // Até 2 colunas; cai para 1 quando cada uma ficaria com menos de 220px (ex. tablet)
  grid-template-columns: repeat(auto-fit, minmax(min(100%, max(220px, calc(50% - 0.5rem))), 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

/**
 * @param {Function} [props.onCPFBlur] - Blur específico do CPF/CNPJ (verificação de documento); usa onBlur se não informado
 */
export const PersonalInfoSection = ({ values, errors, touched, onChange, onBlur, onCPFBlur, opcoes }) => {
  const { buscarMunicipios, minChars } = useMunicipioSearch(opcoes.estados);

  const handleCPFChange = (e) => {
    const formatted = formatCPFCNPJ(e.target.value);
    onChange({ target: { name: 'cpfCnpj', value: formatted } });
  };

  const handleMunicipioNaturalidadeSelect = (mun) => {
    onChange({ target: { name: 'municipioNaturalidadeNome', value: getMunicipioLabel(mun) } });
  };

  return (
    <Section>
      <Header>
        <Icon><FaUser /></Icon>
        <div>
          <Title>Informações Pessoais</Title>
          <Description>Dados pessoais do usuário</Description>
        </div>
      </Header>

      <Input
        label="Nome Completo"
        name="nome"
        value={values.nome}
        onChange={onChange}
        onBlur={onBlur}
        error={touched.nome && errors.nome}
        required
        placeholder="Digite seu nome completo"
      />

      {/* Sistema atual só suporta pessoa física; tipoPessoa: 'F' é fixado em RegisterForm.handleSubmitForm */}
      <Input
        label="Tipo Pessoa"
        value="Pessoa Física"
        disabled
        helper="Campo fixo para pessoa física"
      />

      <Input
        label="Nome Social"
        name="nomeSocial"
        value={values.nomeSocial}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="Opcional"
      />

      <Input
        label="CPF"
        name="cpfCnpj"
        value={values.cpfCnpj}
        onChange={handleCPFChange}
        onBlur={onCPFBlur || onBlur}
        error={touched.cpfCnpj && errors.cpfCnpj}
        required
        placeholder="000.000.000-00"
        maxLength={14}
        success={!errors.cpfCnpj && values.cpfCnpj &&
                 unformat(values.cpfCnpj).length === 11 && 'CPF válido'}
      />

      <Row>
        <Select
          label="Sexo"
          name="sexo"
          value={values.sexo}
          onChange={onChange}
          required
        >
          {SEXO_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        <Input
          label="Data de Nascimento"
          type="date"
          name="dataNascimento"
          value={values.dataNascimento}
          onChange={onChange}
          onBlur={onBlur}
          error={touched.dataNascimento && errors.dataNascimento}
          required
        />
      </Row>

      <Select
        label="Estado Civil"
        name="estadoCivil"
        value={values.estadoCivil}
        onChange={onChange}
        required
        showClear={values.estadoCivil !== '8'}
        onClear={() => onChange({ target: { name: 'estadoCivil', value: '8' } })}
      >
        {ESTADO_CIVIL_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>

      <SearchableSelect
        label="Natural de (Município)"
        name="municipioNaturalidade"
        value={values.municipioNaturalidade}
        valueLabel={values.municipioNaturalidadeNome}
        onChange={onChange}
        onSelectOption={handleMunicipioNaturalidadeSelect}
        onSearch={buscarMunicipios}
        minChars={minChars}
        onBlur={onBlur}
        error={touched.municipioNaturalidade && errors.municipioNaturalidade}
        required
        placeholder="Digite o nome da cidade..."
        getOptionLabel={getMunicipioLabel}
        getOptionValue={(mun) => mun ? String(mun.id) : ''}
        noOptionsMessage="Nenhuma cidade encontrada"
      />

      <Input
        label="Nome da Mãe"
        name="nomeMae"
        value={values.nomeMae}
        onChange={onChange}
        onBlur={onBlur}
        error={touched.nomeMae && errors.nomeMae}
        required
        placeholder="Nome completo da mãe"
      />

      <Input
        label="Nome do Pai"
        name="nomePai"
        value={values.nomePai}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="Opcional"
      />
    </Section>
  );
};
